import { useEffect, useRef, useState } from "react";
import { FileDown, Loader2, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { generateMonthPDF, generateYearPDF, generateDayPDF } from "@/lib/pdf/generate";
import { generateHijriMonthPDF, generateHijriYearPDF, generateHijriDayPDF } from "@/lib/pdf/generate-hijri";
import { downloadPDF } from "@/lib/pdf/pdf-shared";
import { MONTH_NAMES_ID, monthSlug, dateSlug, MONTH_SLUGS_ID } from "@/lib/calendar/dates";
import { HIJRI_MONTHS, hijriMonthSlug } from "@/lib/calendar/hijri";
import { DISCLAIMER_SKB } from "@/data/holidays";

type Mode = "month" | "year" | "day" | "h-month" | "h-year" | "h-day";

function isHijriMode(m: Mode) { return m === "h-month" || m === "h-year" || m === "h-day"; }

const LOGO_STORAGE_KEY = "pdf-logo-custom";
const LOGO_MAX_BYTES = 1024 * 1024;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function dataUrlToFile(dataUrl: string, name: string, type: string): File | null {
  try {
    const [meta, b64] = dataUrl.split(",");
    if (!meta || !b64) return null;
    const bin = atob(b64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return new File([buf], name, { type: type || "image/png" });
  } catch {
    return null;
  }
}

interface Props {
  mode: Mode;
  year: number;
  month?: number;
  day?: number;
  hYear?: number;
  hMonth?: number;
  hDay?: number;
  /** Allow user to pick mode in the dialog */
  allowSwitch?: boolean;
}

export function PdfExportButton(props: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoMode, setLogoMode] = useState<"default" | "custom" | "none">("default");
  const [mode, setMode] = useState<Mode>(props.mode);
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem("pdf-logo-mode");
    if (saved === "default" || saved === "custom" || saved === "none") setLogoMode(saved);
    // Restore previously uploaded custom logo (stored as data URL).
    try {
      const raw = window.localStorage.getItem("pdf-logo-custom");
      if (raw) {
        const parsed = JSON.parse(raw) as { name: string; type: string; dataUrl: string };
        const file = dataUrlToFile(parsed.dataUrl, parsed.name, parsed.type);
        if (file) {
          setLogo(file);
          setLogoDataUrl(parsed.dataUrl);
        }
      }
    } catch {
      /* ignore corrupt storage */
    }
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("pdf-logo-mode", logoMode);
  }, [logoMode]);

  // Revoke preview URL on close / unmount.
  useEffect(() => {
    return () => {
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
    };
  }, []);
  useEffect(() => {
    if (!open && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      lastUrlRef.current = null;
    }
  }, [open, previewUrl]);

  async function buildPdf(): Promise<{ bytes: Uint8Array; name: string }> {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const useDefaultLogo = logoMode === "default";
    const logoFile = logoMode === "custom" ? logo : null;
    let bytes: Uint8Array;
    let name: string;
    if (mode === "year") {
      bytes = await generateYearPDF(props.year, { logo: logoFile, useDefaultLogo, siteUrl: `${origin}/kalender/${props.year}` });
      name = `kalender-${props.year}.pdf`;
    } else if (mode === "month") {
      const m = props.month ?? 1;
      bytes = await generateMonthPDF(props.year, m, { logo: logoFile, useDefaultLogo, siteUrl: `${origin}/kalender/${props.year}/${MONTH_SLUGS_ID[m - 1]}` });
      name = `kalender-${monthSlug(props.year, m)}.pdf`;
    } else if (mode === "day") {
      const m = props.month ?? 1, d = props.day ?? 1;
      bytes = await generateDayPDF(props.year, m, d, { logo: logoFile, useDefaultLogo, siteUrl: `${origin}/kalender/${props.year}/${d}-${MONTH_SLUGS_ID[m - 1]}` });
      name = `kalender-${dateSlug(props.year, m, d)}.pdf`;
    } else if (mode === "h-year") {
      const hy = props.hYear ?? props.year;
      bytes = await generateHijriYearPDF(hy, { logo: logoFile, useDefaultLogo, siteUrl: `${origin}/kalender-hijriah/${hy}/` });
      name = `kalender-hijriah-${hy}.pdf`;
    } else if (mode === "h-month") {
      const hy = props.hYear ?? props.year;
      const hm = props.hMonth ?? 1;
      bytes = await generateHijriMonthPDF(hy, hm, { logo: logoFile, useDefaultLogo, siteUrl: `${origin}/kalender-hijriah/${hy}/${hm}/` });
      name = `kalender-hijriah-${hijriMonthSlug(hy, hm)}.pdf`;
    } else {
      const hy = props.hYear ?? props.year;
      const hm = props.hMonth ?? 1;
      const hd = props.hDay ?? 1;
      bytes = await generateHijriDayPDF(hy, hm, hd, { logo: logoFile, useDefaultLogo, siteUrl: `${origin}/kalender-hijriah/${hy}/${hm}/` });
      name = `kalender-hijriah-${hd}-${hijriMonthSlug(hy, hm)}.pdf`;
    }
    return { bytes, name };
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      const { bytes } = await buildPdf();
      // Copy into a fresh ArrayBuffer so the Blob is not backed by a SharedArrayBuffer-typed view.
      const ab = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(ab).set(bytes);
      const blob = new Blob([ab], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
      lastUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      console.error("PDF preview failed:", err);
      toast.error("Gagal membuat preview. Coba lagi.");
    } finally {
      setPreviewing(false);
    }
  }

  async function handleExport() {
    setBusy(true);
    try {
      const { bytes, name } = await buildPdf();
      downloadPDF(bytes, name);
      setOpen(false);
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("Gagal membuat PDF. Coba lagi atau pilih opsi tanpa logo.");
    } finally {
      setBusy(false);
    }
  }

  const label = (() => {
    if (mode === "year") return `tahun ${props.year}`;
    if (mode === "month") return `${MONTH_NAMES_ID[(props.month ?? 1) - 1]} ${props.year}`;
    if (mode === "day") return `${props.day} ${MONTH_NAMES_ID[(props.month ?? 1) - 1]} ${props.year}`;
    if (mode === "h-year") return `tahun ${props.hYear ?? props.year} H`;
    if (mode === "h-month") return `${HIJRI_MONTHS[(props.hMonth ?? 1) - 1]} ${props.hYear ?? props.year} H`;
    return `${props.hDay} ${HIJRI_MONTHS[(props.hMonth ?? 1) - 1]} ${props.hYear ?? props.year} H`;
  })();
  const hijri = isHijriMode(mode);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FileDown className="mr-2 h-4 w-4" /> Download PDF
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Download Kalender PDF</DialogTitle>
          <DialogDescription>
            Export kalender {label} ke PDF. Bisa tambahkan logo Anda sendiri.
          </DialogDescription>
        </DialogHeader>

        {props.allowSwitch && (
          <div>
            <Label className="mb-2 block">Mode export</Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as Mode)} className="grid grid-cols-3 gap-2">
              {!hijri && (<>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="year" /> Setahun
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="month" /> Per bulan
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="day" /> Per tanggal
              </Label>
              </>)}
              {hijri && (<>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="h-year" /> Setahun H
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="h-month" /> Per bulan H
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
                <RadioGroupItem value="h-day" /> Per tanggal H
              </Label>
              </>)}
            </RadioGroup>
          </div>
        )}

        <div>
          <Label className="mb-2 block">Logo</Label>
          <RadioGroup value={logoMode} onValueChange={(v) => setLogoMode(v as typeof logoMode)} className="grid gap-2">
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="default" /> Logo Kalender Indonesia (default)
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="custom" /> Upload logo sendiri
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="none" /> Tanpa logo
            </Label>
          </RadioGroup>
          {logoMode === "custom" && (
            <div className="mt-2 space-y-2">
              <Input
                id="logo"
                type="file"
                accept="image/png,image/jpeg"
                onChange={async (e) => {
                  const f = e.target.files?.[0] ?? null;
                  if (!f) return;
                  if (f.size > LOGO_MAX_BYTES) {
                    toast.error("Logo terlalu besar (maks 1MB).");
                    return;
                  }
                  try {
                    const dataUrl = await fileToDataUrl(f);
                    setLogo(f);
                    setLogoDataUrl(dataUrl);
                    if (typeof window !== "undefined") {
                      window.localStorage.setItem(
                        LOGO_STORAGE_KEY,
                        JSON.stringify({ name: f.name, type: f.type, dataUrl }),
                      );
                    }
                  } catch {
                    toast.error("Gagal membaca file logo.");
                  }
                }}
              />
              {logoDataUrl && (
                <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 p-2">
                  <img
                    src={logoDataUrl}
                    alt="Preview logo"
                    className="h-10 w-10 rounded object-contain bg-background"
                  />
                  <div className="min-w-0 flex-1 text-xs text-muted-foreground">
                    <p className="truncate font-medium text-foreground">{logo?.name ?? "Logo tersimpan"}</p>
                    <p>Tersimpan otomatis di perangkat Anda.</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setLogo(null);
                      setLogoDataUrl(null);
                      if (typeof window !== "undefined") {
                        window.localStorage.removeItem(LOGO_STORAGE_KEY);
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Hapus logo</span>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
          {DISCLAIMER_SKB}
        </p>

        {previewUrl && (
          <div className="space-y-2">
            <div className="overflow-hidden rounded-md border border-border">
              <iframe
                src={previewUrl}
                title={`Preview PDF ${label}`}
                className="h-[420px] w-full bg-muted"
              />
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={handleExport} disabled={busy}>
                {busy ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Mengunduh...</>
                ) : (
                  <><FileDown className="mr-2 h-4 w-4" /> Download PDF</>
                )}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
          <Button
            variant="secondary"
            onClick={handlePreview}
            disabled={previewing || busy || (logoMode === "custom" && !logo)}
          >
            {previewing ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Membuat preview...</>
            ) : (
              <><Eye className="mr-2 h-4 w-4" /> Preview</>
            )}
          </Button>
          <Button
            onClick={handleExport}
            disabled={busy || (logoMode === "custom" && !logo)}
          >
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyiapkan...</>
            ) : (
              "Download"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}